/**
 * MediaPreprocessor Component
 * Shows progress while extracting audio from large video files
 */

import React, { useEffect, useState } from 'react';
import { MediaProcessor, ProcessingProgress, ExtractedMedia } from '../../services/mediaProcessor';

interface MediaPreprocessorProps {
  file: File;
  onComplete: (result: ExtractedMedia) => void;
  onCancel: () => void;
  onError: (error: string) => void;
}

export const MediaPreprocessor: React.FC<MediaPreprocessorProps> = ({
  file,
  onComplete,
  onCancel,
  onError,
}) => {
  const [progress, setProgress] = useState<ProcessingProgress>({
    stage: 'loading',
    percent: 0,
    message: 'Initializing...',
  });
  const [isProcessing, setIsProcessing] = useState(false);

  const fileSizeMB = (file.size / (1024 * 1024)).toFixed(1);
  const estimatedTime = MediaProcessor.getEstimatedTime(file.size);

  useEffect(() => {
    let cancelled = false;
    const processor = new MediaProcessor();

    const startProcessing = async () => {
      setIsProcessing(true);

      processor.setProgressCallback((p) => {
        if (!cancelled) {
          setProgress(p);
        }
      });

      try {
        const result = await processor.processVideoFile(file);
        if (!cancelled) {
          onComplete(result);
        }
      } catch (err) {
        if (!cancelled) {
          onError(err instanceof Error ? err.message : 'Processing failed');
        }
      } finally {
        processor.cleanup();
      }
    };

    startProcessing();

    return () => {
      cancelled = true;
      processor.cleanup();
    };
  }, [file, onComplete, onError]);

  const stageLabels: Record<ProcessingProgress['stage'], string> = {
    'loading': 'Loading Video',
    'extracting-audio': 'Extracting Audio',
    'extracting-frames': 'Capturing Frames',
    'compressing': 'Compressing',
    'complete': 'Complete',
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-[32px] shadow-2xl max-w-lg w-full overflow-hidden">
        {/* Header */}
        <div className="px-8 py-6 border-b border-slate-100">
          <h3 className="text-lg font-black text-slate-900 uppercase tracking-wider">
            Processing Large File
          </h3>
          <p className="text-xs text-slate-500 mt-1">
            Extracting audio for transcription
          </p>
        </div>

        {/* Content */}
        <div className="p-8 space-y-6">
          {/* File info */}
          <div className="p-4 bg-slate-50 rounded-2xl">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-indigo-100 rounded-xl flex items-center justify-center">
                <svg className="w-6 h-6 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-slate-900 truncate">{file.name}</p>
                <p className="text-xs text-slate-500">{fileSizeMB} MB • Est. {estimatedTime}</p>
              </div>
            </div>
          </div>

          {/* Progress */}
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-sm font-bold text-slate-700">
                {stageLabels[progress.stage]}
              </span>
              <span className="text-sm font-mono text-indigo-600">
                {Math.round(progress.percent)}%
              </span>
            </div>
            
            <div className="h-3 bg-slate-100 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-indigo-600 to-indigo-400 transition-all duration-300 ease-out"
                style={{ width: `${progress.percent}%` }}
              />
            </div>

            <p className="text-xs text-slate-500 text-center">
              {progress.message}
            </p>
          </div>

          {/* Info box */}
          <div className="p-4 bg-indigo-50 border border-indigo-100 rounded-xl">
            <div className="flex gap-3">
              <svg className="w-5 h-5 text-indigo-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="text-xs text-indigo-700">
                <p className="font-bold mb-1">What's happening?</p>
                <p>Your video is being processed locally in your browser. The audio track is extracted for transcription, and key frames are captured so speaker names shown on screen can be read.</p>
              </div>
            </div>
          </div>

          {/* Processing animation */}
          {isProcessing && progress.stage !== 'complete' && (
            <div className="flex justify-center">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <div className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <div className="w-2 h-2 bg-indigo-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-8 py-6 border-t border-slate-100 bg-slate-50">
          <button
            onClick={onCancel}
            className="w-full py-3 text-slate-600 font-bold rounded-xl hover:bg-slate-200 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};